import "reflect-metadata";
import { container, inject, injectWithTransform, singleton } from "tsyringe";
import Cookies from "js-cookie";
import { v4 as uuidv4 } from "uuid";
import { GeoCookie, Logger, LoggerTransformer } from "@identity/utils";
import {
  AgeGateHandler,
  Consents,
  HemTrackingService,
  TrackingContextSetupService,
  ConsentResolver,
  OptInService,
  InstantConfigService,
  CommunicationService,
  MetricReporterService,
  Action,
  PpidProvider,
  eventsRepository,
  CoppaSubject,
} from "@identity/services";
import { ConsentCallbackQueueResolver } from "./services/ConsentService/consentCallbackQueueResolver";
import { ConsentCallbackQueue } from "./services/ConsentService/consentCallbackQueue";
import { COOKIES, UUID, WINDOW } from "./symbols";

container.register(COOKIES, { useValue: Cookies });
container.register(UUID, { useValue: uuidv4 });
container.register(WINDOW, { useValue: window });

@singleton()
class IdentityEngine {
  constructor(
    @inject(GeoCookie) private geoCookie: GeoCookie,
    @inject(AgeGateHandler)
    private readonly ageGateHandler: AgeGateHandler,
    @inject(CommunicationService)
    private readonly communicationService: CommunicationService,
    @inject(OptInService)
    private readonly optInService: OptInService,
    @inject(HemTrackingService)
    private readonly hemTrackingService: HemTrackingService,
    @inject(TrackingContextSetupService)
    private readonly trackingContextSetupService: TrackingContextSetupService,
    @inject(ConsentResolver)
    private readonly consentResolver: ConsentResolver,
    @inject(InstantConfigService)
    private readonly instantConfigService: InstantConfigService,
    @inject(WINDOW)
    private readonly window: Window,
    @injectWithTransform(Logger, LoggerTransformer, "IdentityEngine")
    private readonly logger: Logger,
    @inject(PpidProvider) private ppidProvider: PpidProvider,
    @inject(ConsentCallbackQueue)
    private consentCallbackQueue: ConsentCallbackQueue,
    @inject(ConsentCallbackQueueResolver)
    private consentCallbackQueueResolver: ConsentCallbackQueueResolver,
    @inject(CoppaSubject)
    private coppaSubject: CoppaSubject,
    @inject(MetricReporterService)
    private metricReporterService: MetricReporterService
  ) {}

  private async initializeAgeGateHandler(): Promise<void> {
    return this.ageGateHandler.init(this.communicationService);
  }

  private updateGTMConsent(granted: boolean): void {
    if (this.window.dataLayer === undefined) {
      this.window.dataLayer = [];
    }

    this.window.dataLayer.push({
      event: "user-consent",
      consent: granted.toString(),
    });
  }

  private async initializeTracking() {
    this.logger.info("Setting up tracking");
    this.optInService.trackingAllowed = true;
    this.hemTrackingService.activateTracking();
    await this.ppidProvider.setPpid();
    this.communicationService.emit(new Action(eventsRepository.IDENTITY_READY));
    this.metricReporterService.send("ready");
  }

  public async init(): Promise<void> {
    await this.geoCookie.setGeoCookieIfNotExist();
    await this.instantConfigService.init();
    this.optInService.init();
    this.metricReporterService.init(this.instantConfigService);
    this.metricReporterService.send("init");
  }

  public run(): void {
    // PACKAGE_VERSION will be replaced with version from package.json
    this.logger.info("Starting identity engine vPACKAGE_VERSION");
    const domain = this.window.location.hostname;
    this.communicationService.once(
      eventsRepository.CONSENT_READY,
      (consents: Consents) => {
        this.trackingContextSetupService.setup(domain);
        this.consentResolver.resolve(
          consents,
          async () => {
            this.updateGTMConsent(true);
            await this.initializeAgeGateHandler();
            this.coppaSubject.resolveCoppaStatus();
            await this.initializeTracking();
          },
          () => {
            this.updateGTMConsent(false);
            this.coppaSubject.resolveCoppaStatus();
            this.optInService.trackingAllowed = false;
            this.communicationService.emit(
              new Action(eventsRepository.IDENTITY_READY)
            );
            this.metricReporterService.send("ready");
            this.logger.info("Tracking rejected");
          }
        );
      }
    );
  }
}

function initialize() {
  const identityEngine = container.resolve(IdentityEngine);
  identityEngine.init().then(() => {
    identityEngine.run();
  });
}

initialize();
